import React, { useEffect, Fragment, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { GetAreas } from "../../../../api/masters/areaAPI";
import { GetPreviousOpds, StoreOpdEntry } from "../../../../api/opd/opdEntryAPI";
import { GetDepartments } from "../../../../api/masters/departmentAPI";
import { GetCases } from "../../../../api/masters/caseAPI";
import { GetConsultants } from "../../../../api/masters/consultantAPI";
import { GetLastEntry } from "../../../../api/common/common";
import { GetPatients } from "../../../../api/masters/patientAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";
import { calculateDaysFromToday, formatDate } from "../../../../utils/dateFormatter";
import Select from "react-select";

// Validation schema (basic example — you can expand this)
const schema = yup.object().shape({
	// ===== Visit details =====
	docType: yup.string().required("Document type is required"),
	opdNo: yup.string().required("OPD number is required"),
	date: yup.date().required("Date is required"),
	time: yup.string().required("Time is required"),
	uhidNo: yup.string().required("UHID number is required"),

	// ===== Patient details =====
	patientName: yup.string().required("Patient name is required"),
	age: yup.string().required("Age is required"),

	mobileNo: yup
		.string(),

	// // ===== Doctor info =====
	caseId: yup.string().required("Case is required"),
	departmentId: yup.string().nullable(),
	consultantDoctorId: yup.string().required("Consultant doctor is required"),
	referenceDoctorId: yup.string().required("Reference doctor is required"),

	// ===== Billing info =====
	amount: yup
		.number()
		.required("Amount is required")
		.min(0, "Amount cannot be negative"),

	netAmount: yup
		.number()
		.required("Net amount is required")
		.min(0, "Net amount cannot be negative"),
	paymentMode: yup.string().required("Payment mode is required"),
});

const AddOpdEntry = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [consultants, setConsultants] = useState([]);
	const [areas, setAreas] = useState([]);
	const [departments, setDepartments] = useState([]);
	const [cases, setCases] = useState([]);
	const [profile, setProfile] = useState("");
	const [profilePreview, setProfilePreview] = useState("");
	const [oldPatients, setOldPatients] = useState([]);
	const [lastPatientEntry, setLastPatientEntry] = useState({});
	const [previousOpds, setPreviousOpds] = useState([]);

	const handleFileChange = event => {
		const file = event.target.files[0]; // Extract the first file
		if (file) {
			setProfilePreview(URL.createObjectURL(file)); // Set preview
			setProfile(file);
		}
	};

	const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			docType: "OPD",
			serviceTax: 0,
			discountPercentage: 0,
			discountAmount: 0,
			paymentMode: "Cash",
		}
	});

	const fetchConsultants = async () => {
		try {
			const response = await GetConsultants();
			if (response.success) {
				setConsultants(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchAreas = async () => {
		try {
			const response = await GetAreas();
			if (response.success) {
				setAreas(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchDepartments = async () => {
		try {
			const response = await GetDepartments();
			if (response.success) {
				setDepartments(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchCases = async () => {
		try {
			const response = await GetCases();
			if (response.success) {
				setCases(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchLastEntry = async () => {
		try {
			const response = await GetLastEntry();
			if (response.success) {
				setLastPatientEntry(response.data.lastPatientEntry);
				setValue(
					"opdNo",
					String(response?.data?.lastOpdEntry?.opdNo ? response.data.lastOpdEntry.opdNo + 1 : 1)
				);

				setValue(
					"uhidNo",
					String(response?.data?.lastPatientEntry?.uhidNo ? response.data.lastPatientEntry.uhidNo + 1 : 1001)
				);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	useEffect(() => {
		fetchConsultants();
		fetchAreas();
		fetchDepartments();
		fetchCases();
		fetchLastEntry();
	}, []);

	const fetchOldPatients = async () => {
		try {
			const response = await GetPatients();
			if (response.success) {
				setOldPatients(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchPreviousOpds = async (patientId) => {
		try {
			const response = await GetPreviousOpds({ patientId });
			if (response.success) {
				const opdData = response.data;

				setPreviousOpds(opdData);

				if (opdData?.length > 0) {
					const last = opdData[0];

					setValue("consultantDoctorId", last?.consultantDoctorId?._id);
					setValue("referenceDoctorId", last?.referenceDoctorId?._id);
					setValue("caseId", last?.caseId?._id);

					// 👇 Pass opdData directly
					updateAmountByConsultant(
						last?.consultantDoctorId?._id,
						opdData
					);
				}
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const oldPatientValue = watch("oldPatient");
	const amount = watch("amount");
	const serviceTax = watch("serviceTax");
	const discountPercentage = watch("discountPercentage");
	const discountAmount = watch("discountAmount");

	useEffect(() => {
		const a = parseFloat(amount) || 0;
		const d = parseFloat(discountAmount) || 0;
		const s = parseFloat(serviceTax) || 0;

		setValue("netAmount", a + s - d);
	}, [amount, discountAmount, serviceTax]);

	const setPatientDetails = patient => {
		// console.log("Patient", patient, lastPatientEntry);
		setValue("uhidNo", patient?.uhidNo || (lastPatientEntry?.uhidNo ? lastPatientEntry.uhidNo + 1 : 1001));
		setValue("patientNamePrefix", patient?.patientNamePrefix || "Mr.");
		setValue("patientName", patient?.patientName || "");
		setValue("motherName", patient?.motherName || "");
		setValue("guardianNamePrefix", patient?.guardianNamePrefix || "Mr.");
		setValue("guardianName", patient?.guardianName || "");
		setValue("dob", patient?.dob || "");
		setValue("age", patient?.age || "");
		setValue("ageSuffix", patient?.ageSuffix || "Years");
		setValue("sex", patient?.sex || "Male");
		setValue("maritalStatus", patient?.maritalStatus || "Single");
		setValue("bloodGroup", patient?.bloodGroup || "A+");
		setValue("religion", patient?.religion || "");
		setValue("caste", patient?.caste || "");
		setValue("foreiger", patient?.foreiger || "No");

		setValue("address", patient?.address || "");
		setValue("areaId", patient?.areaId || "");
		setValue("mobileNo", patient?.mobileNo || "");
		setValue("phoneNo", patient?.phoneNo || "");
		setValue("email", patient?.email || "");
	};

	useEffect(() => {
		if (oldPatientValue === "Yes") {
			fetchOldPatients();
			setValue("uhidNo", "");
		} else {
			setPatientDetails({});
			setPreviousOpds([]);
			setValue("consultantDoctorId","")
			setValue("referenceDoctorId","")
			setValue("caseId","")
		}
	}, [oldPatientValue]);

	const updateAmountByConsultant = (consultantId, opdList = previousOpds) => {
		if (!consultantId) return;

		const consultant = consultants.find(cons => cons._id === consultantId);
		if (!consultant) return;

		const lastOpd = opdList.find(
			opd =>
				opd.consultantDoctorId?._id === consultantId &&
				opd.amount > 0
		);

		if (lastOpd) {
			const diffDays = calculateDaysFromToday(lastOpd.date);

			if (diffDays <= consultant.opdValidUpTo) {
				setValue("amount", consultant?.opdChargesBetweenValidityPeriod || 0);
			} else {
				setValue("amount", consultant?.opdCharges || 0);
			}
		} else {
			setValue("amount", consultant?.opdCharges || 0);
		}
	};

	const onSubmit = async data => {
		try {
			dispatch({ type: "LOADER_START" });
			const formData = new FormData();

			for (const key in data) {
				if (data[key] !== "profile") {
					formData.append(key, data[key]);
				}
			}
			if (profile) {
				formData.append("profile", profile);
			}

			const response = await StoreOpdEntry(formData);
			if (response.success) {
				showToast("success", response.message);
				// navigate(`/opd/transaction/opd-entry/list`);
				navigate(`/opd/transaction/opd-entry/view/${response?.data?._id}`);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		} finally {
			dispatch({ type: "LOADER_STOP" });
		}
	};

	const formRef = useRef(null);
	useKeyboardSubmit(handleSubmit, onSubmit);
	useEnterNavigation(formRef);

	return (
		<Fragment>
			<Breadcrumb parent="OPD" title="Add OPD Entry" />
			<div className="container-fluid">
				<div className="row">
					<div className="col-sm-12">
						<div className="card">
							<div className="card-header d-flex justify-content-between">
								<h5>Add OPD Entry</h5>
								<Link
									to={`/opd/transaction/opd-entry/list`}
								>
									<button className="btn btn-primary" id="list">
										List OPD Entry
									</button>
									<UncontrolledTooltip placement="top" target="list">
										List OPD Entry
									</UncontrolledTooltip>
								</Link>
							</div>

							<form
								ref={formRef}
								className="form theme-form"
								onSubmit={handleSubmit(onSubmit)}
							>
								<div className="card-body">
									<div className="row">
										{/* ========================= BASIC INFO ========================= */}
										<div className="col-12">
											<h5 className="mt-3 mb-3 text-primary">
												Basic Information
											</h5>
										</div>

										<div className="col-md-4 mb-3">
											<label>Doc Type *</label>
											<input
												className="form-control"
												readOnly
												{...register("docType")}
											/>
											{errors.docType &&
												<p className="text-danger">
													{errors.docType.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>OPD No</label>
											<input className="form-control" {...register("opdNo")} readOnly />
											{errors.opdNo &&
												<p className="text-danger">
													{errors.opdNo.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Date *</label>
											<input
												type="date"
												className="form-control"
												defaultValue={new Date().toISOString().slice(0, 10)}
												{...register("date")}
											/>
											{errors.date &&
												<p className="text-danger">
													{errors.date.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Time *</label>
											<input
												type="time"
												className="form-control"
												defaultValue={new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
												{...register("time")}
											/>
											{errors.time &&
												<p className="text-danger">
													{errors.time.message}
												</p>}
										</div>

										

										<div className="col-md-4 mb-3">
											<label>Casually</label>
											<select
												className="form-control"
												{...register("casually")}
											>
												<option value="No">No</option>
												<option value="Yes">Yes</option>
											</select>
											{errors.casually &&
												<p className="text-danger">
													{errors.casually.message}
												</p>}
										</div>

										{/* ========================= PATIENT DETAILS ========================= */}
										<div className="col-12">
											<h5 className="mt-4 mb-3 text-primary">
												Patient Details
											</h5>
										</div>

										<div className="col-md-4 mb-3">
											<label>Old Patient</label>
											<select
												className="form-control"
												{...register("oldPatient")}
											>
												<option value="No">No</option>
												<option value="Yes">Yes</option>
											</select>
											{errors.oldPatient &&
												<p className="text-danger">
													{errors.oldPatient.message}
												</p>}
										</div>
										{oldPatientValue === "Yes" && (
											<div className="col-md-4 mb-3">
												<label>Select Old Patient</label>

												<Select
													options={oldPatients.map((patient) => ({
														value: patient._id,
														label: `${patient.patientName} - ${patient.uhidNo}`,
														patientData: patient, // store full object
													}))}
													placeholder="Search Patient..."
													onChange={(selectedOption) => {
														if (!selectedOption) {
															setValue("oldPatientId", "");
															return;
														}

														const selected = selectedOption.patientData;

														// set id in form
														setValue("oldPatientId", selected._id);

														setPatientDetails(selected);

														fetchPreviousOpds(selected._id);
													}}
												/>
											</div>
										)}

										<div className="col-md-4 mb-3">
											<label>UHID No *</label>
											<input className="form-control" {...register("uhidNo")} readOnly />
											{errors.uhidNo &&
												<p className="text-danger">
													{errors.uhidNo.message}
												</p>}
										</div>
										<div className="clearfix"></div>
										<div className="col-md-4 mb-3">
											<label>Patient Name *</label>
											<div className="input-group">
												<select
													className="form-select"
													style={{ maxWidth: "90px" }}
													{...register("patientNamePrefix")}
												>
													<option value="Mr.">Mr.</option>
													<option value="Mrs.">Mrs.</option>
													<option value="Ms.">Ms.</option>
													<option value="Dr.">Dr.</option>
												</select>
												<input
													type="text"
													className="form-control"
													placeholder="Patient Name"
													{...register("patientName")}
												/>
											</div>
											{errors.patientName &&
												<p className="text-danger">
													{errors.patientName.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Mother Name</label>
											<input
												className="form-control"
												{...register("motherName")}
											/>
											{errors.motherName &&
												<p className="text-danger">
													{errors.motherName.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Guardian Name</label>
											<div className="input-group">
												<select
													className="form-select"
													style={{ maxWidth: "90px" }}
													{...register("guardianNamePrefix")}
												>
													<option value="Mr.">Mr.</option>
													<option value="Mrs.">Mrs.</option>
													<option value="Ms.">Ms.</option>
													<option value="Dr.">Dr.</option>
												</select>
												<input
													type="text"
													className="form-control"
													placeholder="Guardian Name"
													{...register("guardianName")}
												/>
											</div>
											{errors.guardianName &&
												<p className="text-danger">
													{errors.guardianName.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>DOB</label>
											<input
												type="date"
												className="form-control"
												{...register("dob")}
											/>
											{errors.dob &&
												<p className="text-danger">
													{errors.dob.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Age</label>
											<div className="input-group">
												<input
													type="number"
													step="any"
													className="form-control"
													{...register("age")}
												/>
												<select
													className="form-select"
													style={{ maxWidth: "100px" }}
													{...register("ageSuffix")}
												>
													<option value="Years">Years</option>
													<option value="Months">Months</option>
													<option value="Days">Days</option>
												</select>
											</div>
											{errors.age &&
												<p className="text-danger">
													{errors.age.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Sex</label>
											<select className="form-control" {...register("sex")}>
												<option value="Male">Male</option>
												<option value="Female">Female</option>
												<option value="Other">Other</option>
											</select>
											{errors.sex &&
												<p className="text-danger">
													{errors.sex.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Marital Status</label>
											<select
												className="form-control"
												{...register("maritalStatus")}
											>
												<option value="">Select</option>
												<option value="Single">Single</option>
												<option value="Married">Married</option>
												<option value="Divorced">Divorced</option>
												<option value="Widowed">Widowed</option>
											</select>
											{errors.maritalStatus &&
												<p className="text-danger">
													{errors.maritalStatus.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Blood Group</label>
											<select
												className="form-control"
												{...register("bloodGroup")}
											>
												<option value="">Select</option>
												{[
													"A+",
													"A-",
													"B+",
													"B-",
													"O+",
													"O-",
													"AB+",
													"AB-"
												].map(bg =>
													<option key={bg} value={bg}>
														{bg}
													</option>
												)}
											</select>
											{errors.bloodGroup &&
												<p className="text-danger">
													{errors.bloodGroup.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Religion</label>
											<select
												className="form-control"
												{...register("religion")}
											>
												<option value="">Select</option>
												{[
													"Hindu",
													"Muslim",
													"Sikh",
													"Christian",
													"Buddhist",
													"Jain",
													"Jewish",
													"Other"
												].map(religion =>
													<option key={religion} value={religion}>
														{religion}
													</option>
												)}
											</select>

											{errors.religion &&
												<p className="text-danger">
													{errors.religion.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Caste</label>
											<select className="form-control" {...register("caste")} >
												<option value="">Select</option>
												{[
													"General",
													"OBC",
													"SC",
													"ST",
													"Other"
												].map(caste =>
													<option key={caste} value={caste}>
														{caste}
													</option>
												)}
											</select>

											{errors.caste &&
												<p className="text-danger">
													{errors.caste.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Foreigner</label>
											<select
												className="form-control"
												{...register("foreiger")}
											>
												<option value="No">No</option>
												<option value="Yes">Yes</option>
											</select>
											{errors.foreiger &&
												<p className="text-danger">
													{errors.foreiger.message}
												</p>}
										</div>

										{/* ========================= CONTACT INFO ========================= */}
										<div className="col-12">
											<h5 className="mt-4 mb-3 text-primary">
												Contact Information
											</h5>
										</div>

										<div className="col-md-4 mb-3">
											<label>Address</label>
											<textarea
												className="form-control"
												{...register("address")}
											/>
											{errors.address &&
												<p className="text-danger">
													{errors.address.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Area</label>
											<select className="form-control" {...register("areaId")}>
												<option value="">Select Area</option>
												{areas.map(area =>
													<option key={area._id} value={area._id}>
														{area.name}
													</option>
												)}
											</select>
											{errors.areaId &&
												<p className="text-danger">
													{errors.areaId.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Mobile No</label>
											<input
												className="form-control"
												{...register("mobileNo")}
											/>
											{errors.mobileNo &&
												<p className="text-danger">
													{errors.mobileNo.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Phone No</label>
											<input
												className="form-control"
												{...register("phoneNo")}
											/>
											{errors.phoneNo &&
												<p className="text-danger">
													{errors.phoneNo.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Email</label>
											<input
												type="email"
												className="form-control"
												{...register("email")}
											/>
											{errors.email &&
												<p className="text-danger">
													{errors.email.message}
												</p>}
										</div>

										{/* ========================= MEDICAL INFO ========================= */}
										<div className="col-12">
											<h5 className="mt-4 mb-3 text-primary">
												Medical Information
											</h5>
										</div>

										<div className="col-md-4 mb-3">
											<label>Case *</label>
											<select className="form-control" {...register("caseId")}>
												<option value="">Select</option>
												{cases.map(cas =>
													<option key={cas._id} value={cas._id}>
														{cas.name}
													</option>
												)}
											</select>
											{errors.caseId &&
												<p className="text-danger">
													{errors.caseId.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Reference Doctor *</label>
											<select
												className="form-control"
												{...register("referenceDoctorId")}
											>
												<option value="">Select</option>
												{consultants.map(d =>
													<option key={d._id} value={d._id}>
														{d.name}
													</option>
												)}
											</select>
											{errors.referenceDoctorId &&
												<p className="text-danger">
													{errors.referenceDoctorId.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Consultant Doctor *</label>
											<select
												className="form-control"
												{...register("consultantDoctorId", {
													onChange: (e) => {
														updateAmountByConsultant(e.target.value);
													}
												})}
											>
												<option value="">Select</option>
												{consultants.map(d =>
													<option key={d._id} value={d._id}>
														{d.name}
													</option>
												)}
											</select>

											{errors.consultantDoctorId &&
												<p className="text-danger">
													{errors.consultantDoctorId.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Department</label>
											<select
												className="form-control"
												{...register("departmentId")}
											>
												<option value="">Select</option>
												{departments.map(d =>
													<option key={d._id} value={d._id}>
														{d.name}
													</option>
												)}
											</select>
											{errors.departmentId &&
												<p className="text-danger">
													{errors.departmentId.message}
												</p>}
										</div>

										<div className="col-md-12 mb-3">
											<label>Any Known Allergies</label>
											<textarea
												className="form-control"
												{...register("anyKnownAllergies")}
											/>
											{errors.anyKnownAllergies &&
												<p className="text-danger">
													{errors.anyKnownAllergies.message}
												</p>}
										</div>

										<div className="col-md-12 mb-3">
											<label>Complaints</label>
											<textarea
												className="form-control"
												{...register("complains")}
											/>
											{errors.complains &&
												<p className="text-danger">
													{errors.complains.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Temperature</label>
											<input
												type="number"
												step="any"
												className="form-control"
												{...register("temperature")}
											/>
											{errors.temperature &&
												<p className="text-danger">
													{errors.temperature.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Weight</label>
											<input
												type="number"
												step="any"
												className="form-control"
												{...register("weight")}
											/>
											{errors.weight &&
												<p className="text-danger">
													{errors.weight.message}
												</p>}
										</div>

										{/* ========================= BILLING INFO ========================= */}
										<div className="col-12">
											<h5 className="mt-4 mb-3 text-primary">
												Billing Information
											</h5>
										</div>

										<div className="col-md-4 mb-3">
											<label>Payment Mode</label>
											<select
												className="form-control"
												{...register("paymentMode")}
											>
												<option value="">Select</option>
												<option value="Cash">Cash</option>
												<option value="Online">Online</option>
											</select>
											{errors.paymentMode &&
												<p className="text-danger">
													{errors.paymentMode.message}
												</p>}
										</div>
										<div className="col-md-4 mb-3">
											<label>Amount</label>
											<input
												type="number"
												step="any"
												className="form-control"
												{...register("amount")}
											/>
											{errors.amount &&
												<p className="text-danger">
													{errors.amount.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Service Tax</label>
											<input
												type="number"
												step="any"
												className="form-control"
												{...register("serviceTax")}
											/>
											{errors.serviceTax &&
												<p className="text-danger">
													{errors.serviceTax.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Discount %</label>
											<input
												type="number"
												step="any"
												className="form-control"
												{...register("discountPercentage", {
													onChange: (e) => {
														if (e.target.value > 100) {
															e.target.value = 100;
														}
														const discountAmount = e.target.value * amount / 100;
														setValue("discountAmount", discountAmount);
													}
												})}
											/>
											{errors.discountPercentage &&
												<p className="text-danger">
													{errors.discountPercentage.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Discount Amount</label>
											<input
												type="number"
												step="any"
												className="form-control"
												{...register("discountAmount", {
													onChange: (e) => {
														const discountPercentage = e.target.value * 100 / amount;
														setValue("discountPercentage", discountPercentage);
													}
												})}
											/>
											{errors.discountAmount &&
												<p className="text-danger">
													{errors.discountAmount.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Net Amount</label>
											<input
												type="number"
												step="any"
												className="form-control"
												{...register("netAmount")}
												readOnly
											/>
											{errors.netAmount &&
												<p className="text-danger">
													{errors.netAmount.message}
												</p>}
										</div>

										{/* ========================= OTHER DETAILS ========================= */}
										<div className="col-12">
											<h5 className="mt-4 mb-3 text-primary">Other Details</h5>
										</div>

										<div className="col-md-4 mb-3">
											<label>ID Type</label>
											<input className="form-control" {...register("idType")} />
											{errors.idType &&
												<p className="text-danger">
													{errors.idType.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>ID No</label>
											<input className="form-control" {...register("idNo")} />
											{errors.idNo &&
												<p className="text-danger">
													{errors.idNo.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Profile</label>
											<input
												type="file"
												className="form-control"
												onChange={handleFileChange}
											/>
											{errors.profile &&
												<p className="text-danger">
													{errors.profile.message}
												</p>}
											{profilePreview &&
												<img
													src={profilePreview}
													alt="Preview"
													width={100}
													height={100}
												/>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Organization</label>
											<input
												className="form-control"
												{...register("organization")}
											/>
											{errors.organization &&
												<p className="text-danger">
													{errors.organization.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Card Holder Name</label>
											<input
												className="form-control"
												{...register("cardHolderName")}
											/>
											{errors.cardHolderName &&
												<p className="text-danger">
													{errors.cardHolderName.message}
												</p>}
										</div>

										<div className="col-md-4 mb-3">
											<label>Code/Policy No</label>
											<input
												className="form-control"
												{...register("codePolicyNo")}
											/>
											{errors.codePolicyNo &&
												<p className="text-danger">
													{errors.codePolicyNo.message}
												</p>}
										</div>
									</div>
									<div className="row">
										<div className="col-12">
											<h5 className="mt-4 mb-3 text-primary">Visit Details</h5>
										</div>
										<table className="table table-bordered">
											<thead>
												<tr>
													<th>Date</th>
													<th>Consultant Doctor</th>
													<th>Days</th>
													<th>Amount</th>
												</tr>
											</thead>
											<tbody>
												{previousOpds && previousOpds.map((opd, index) => {
													return (
														<tr key={index}>
															<td>{formatDate(opd.date)}</td>
															<td>{opd.consultantDoctorId?.name}</td>
															<td>{calculateDaysFromToday(opd.date)}</td>
															<td>{opd.amount}</td>
														</tr>
													);
												})}
											</tbody>
										</table>
									</div>
								</div>

								<div className="card-footer text-end">
									<button className="btn btn-primary me-2" type="submit">
										Submit
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

export default AddOpdEntry;
